import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ROLE_LABELS } from "../api/users";
import { useAuth } from "../auth/AuthContext";
import { ChangeOwnPasswordModal } from "./users/ChangeOwnPasswordModal";
import { NotificationsBell } from "./NotificationsBell";
import { ListPageStatsBar, ListPageStatsProvider } from "../context/ListPageStatsContext";
import "./AppShell.css";

type NavItem = {
  to: string;
  label: string;
  roles?: string[];
  moduleKey?: string;
  /** true ise yalnizca tam path (or. /settings, /settings/branches altinda degil) */
  end?: boolean;
  /** true ise yalnizca platform sahibi tenant gorebilir */
  platformOwnerOnly?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  // Egitmen operasyonel ekranlarda kalir: takvim, kursiyer iletisimi, ders raporu, mobil.
  { to: "/dashboard", label: "Kontrol Paneli", roles: ["tenant_admin", "manager", "office_staff"], moduleKey: "dashboard" },
  { to: "/calendar", label: "Takvim & Planlama", moduleKey: "calendar" },
  { to: "/students", label: "Kursiyerler", moduleKey: "students" },
  { to: "/instructors", label: "Eğitmenler", roles: ["tenant_admin", "manager", "office_staff"], moduleKey: "instructors" },
  { to: "/vehicles", label: "Araçlar", roles: ["tenant_admin", "manager", "office_staff"], moduleKey: "vehicles" },
  { to: "/auto-planning", label: "Otomatik Planlama", roles: ["tenant_admin", "manager", "office_staff"], moduleKey: "auto_planning" },
  { to: "/lesson-reports", label: "Ders Detay Raporu", moduleKey: "lesson_reports" },
  { to: "/reports", label: "Raporlar", roles: ["tenant_admin", "manager", "office_staff"], moduleKey: "reports" },
  { to: "/m/instructor", label: "Mobil Görünüm", roles: ["instructor"] },
  {
    to: "/settings",
    label: "Kurum Ayarları",
    roles: ["tenant_admin", "manager", "office_staff"],
    moduleKey: "tenant_settings",
    end: true,
  },
  { to: "/settings/branches", label: "Şubeler", roles: ["tenant_admin", "manager", "office_staff"], moduleKey: "branches" },
  { to: "/audit-logs", label: "Denetim Kayıtları", roles: ["tenant_admin", "manager", "office_staff"], moduleKey: "audit_logs" },
  { to: "/users", label: "Kullanıcılar", roles: ["tenant_admin", "manager", "office_staff"], moduleKey: "users" },
  { to: "/customers", label: "Müşteriler", roles: ["tenant_admin"], platformOwnerOnly: true },
  { to: "/system-status", label: "Sistem Durumu", roles: ["tenant_admin"], platformOwnerOnly: true },
];

export function AppShell() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const role = session?.user?.role;
  const enabledModules = session?.user?.enabled_modules || [];
  const isPlatformOwner = session?.user?.is_platform_owner === true;
  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.platformOwnerOnly && !isPlatformOwner) return false;
    if (!item.roles || (role && item.roles.includes(role))) {
      if (!item.moduleKey) return true;
      if (isPlatformOwner) return true;
      return enabledModules.includes(item.moduleKey);
    }
    return false;
  });
  const activeItem = [...visibleItems]
    .sort((a, b) => b.to.length - a.to.length)
    .find((item) => {
      if (item.end) return location.pathname === item.to;
      return (
        location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
      );
    });
  const pageTitle = activeItem?.label || "KURS PANELİM";
  const isCalendarRoute = location.pathname.startsWith("/calendar");
  const isViewportFillRoute =
    location.pathname.startsWith("/audit-logs") ||
    location.pathname.startsWith("/lesson-reports") ||
    location.pathname.startsWith("/students") ||
    location.pathname === "/instructors" ||
    location.pathname.startsWith("/instructors/") ||
    location.pathname === "/vehicles" ||
    location.pathname.startsWith("/vehicles/") ||
    location.pathname.startsWith("/reports");

  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!navOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [navOpen]);

  const shellClass = [
    "kp-shell",
    isCalendarRoute ? "kp-shell--calendar" : "",
    isViewportFillRoute ? "kp-shell--viewport-fill" : "",
    navOpen ? "kp-shell--nav-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <ListPageStatsProvider>
    <div className={shellClass}>
      {navOpen ? (
        <button
          type="button"
          className="kp-shell__backdrop"
          aria-label="Menüyü kapat"
          onClick={() => setNavOpen(false)}
        />
      ) : null}
      <aside className="kp-shell__sidebar" aria-label="Ana menü" aria-hidden={!navOpen}>
        <div className="kp-shell__sidebar-head">
          <div className="kp-shell__brand">
            <div className="kp-shell__brand-mark">KP</div>
            <div className="kp-shell__brand-text">
              <strong>KURS</strong>
              <span>PANELİM</span>
            </div>
          </div>
          <button
            type="button"
            className="kp-shell__sidebar-close"
            onClick={() => setNavOpen(false)}
            aria-label="Menüyü kapat"
          >
            ×
          </button>
        </div>
        <nav className="kp-shell__nav" id="kp-main-nav">
          {visibleItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `kp-shell__nav-item${isActive ? " kp-shell__nav-item--active" : ""}`
              }
              onClick={() => setNavOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="kp-shell__user">
          <div className="kp-shell__user-info">
            <strong>{session?.user?.full_name || session?.user?.email || "Kullanıcı"}</strong>
            <span>{role ? ROLE_LABELS[role as keyof typeof ROLE_LABELS] ?? role : "-"}</span>
          </div>
          <div className="kp-shell__user-actions">
            <button
              className="kp-btn kp-btn--ghost"
              onClick={() => setShowChangePassword(true)}
              title="Şifremi Değiştir"
            >
              Şifre
            </button>
            <button className="kp-btn kp-btn--ghost" onClick={handleLogout}>
              Çıkış
            </button>
          </div>
        </div>
      </aside>
      <main className="kp-shell__main">
        <header className={`kp-shell__topbar${isCalendarRoute ? " kp-shell__topbar--calendar" : ""}`}>
          <div className="kp-shell__topbar-start">
            <button
              type="button"
              className="kp-shell__menu-btn"
              aria-expanded={navOpen}
              aria-controls="kp-main-nav"
              aria-label={navOpen ? "Menüyü kapat" : "Menüyü aç"}
              onClick={() => setNavOpen((open) => !open)}
            >
              <span className="kp-shell__menu-icon" aria-hidden />
            </button>
            {!isCalendarRoute && <span className="kp-shell__topbar-title">{pageTitle}</span>}
          </div>
          <NotificationsBell />
        </header>
        <div className="kp-shell__content">
          <Outlet />
        </div>
        <ListPageStatsBar />
      </main>
      {showChangePassword && (
        <ChangeOwnPasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </div>
    </ListPageStatsProvider>
  );
}
